import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@contexts/AuthContext'
import ProtectedRoute from '@components/web/ProtectedRoute'
import { usePageTracking } from '@lib/analytics/usePageTracking'

// Layout pages
import RootLayout from '@pages/RootLayout'
import AuthLayout from '@pages/AuthLayout'
import PatientLayout from '@pages/PatientLayout'
import ProviderLayout from '@pages/ProviderLayout'

// Auth pages
import Welcome from '@pages/Welcome'
import Privacy from '@pages/Privacy'
import Terms from '@pages/Terms'
import SignUp from '@pages/auth/SignUp'
import Login from '@pages/auth/Login'
import EmailVerification from '@pages/auth/EmailVerification'
import IdentityVerification from '@pages/auth/IdentityVerification'
import VerificationPending from '@pages/auth/VerificationPending'
import MedicalInfo from '@pages/auth/MedicalInfo'
import ProviderSignUp from '@pages/auth/ProviderSignUp'
import ProviderEmailVerification from '@pages/auth/ProviderEmailVerification'
import ProviderIdentityVerification from '@pages/auth/ProviderIdentityVerification'
import ProviderVerificationPending from '@pages/auth/ProviderVerificationPending'

// Patient pages (placeholders for now)
import PatientDashboard from '@pages/patient/Dashboard'
import ServiceDetail from '@pages/patient/ServiceDetail'
import Appointments from '@pages/patient/Appointments'
import Profile, { PatientTermsPage } from '@pages/patient/Profile'
import ProviderDetail from '@pages/patient/ProviderDetail'
import BookingPage from '@pages/patient/BookingPage'
import AppointmentDetail from '@pages/patient/AppointmentDetail'
import PatientChat from '@pages/patient/Chat'
import NotificationsPage from '@pages/patient/Notifications'
import ChatAppointments from '@pages/patient/ChatAppointments'

// Provider pages (placeholders for now)
import ProviderDashboard from '@pages/provider/Dashboard'
import ProviderAppointments from '@pages/provider/Appointments'
import ProviderAppointmentDetail from '@pages/provider/AppointmentDetail'
import ProviderProfile, { ProviderTermsPage } from '@pages/provider/Profile'
import ProviderChat from '@pages/provider/Chat'
import ProviderChatAppointments from '@pages/provider/ChatAppointments'
import ProviderNotificationsPage from '@pages/provider/Notifications'
import ProviderInduction from '@pages/provider/Induction'

function AppRoutes() {
  usePageTracking()

  return (
        <Routes>
          {/* Root/Welcome Route */}
          <Route element={<RootLayout />}>
            <Route index element={<Welcome />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />
          </Route>

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="auth/signup" element={<SignUp />} />
            <Route path="auth/login" element={<Login />} />
            <Route path="auth/verify-email" element={<EmailVerification />} />
            <Route path="auth/verify-identity" element={<IdentityVerification />} />
            <Route path="auth/verification-pending" element={<VerificationPending />} />
            <Route path="auth/medical-info" element={<MedicalInfo />} />
            <Route path="auth/provider-signup" element={<ProviderSignUp />} />
            <Route path="auth/provider-email-verification" element={<ProviderEmailVerification />} />
            <Route path="auth/provider-identity-verification" element={<ProviderIdentityVerification />} />
            <Route path="auth/provider-verification-pending" element={<ProviderVerificationPending />} />
          </Route>

          {/* Patient Routes (Protected) */}
          <Route
            element={
              <ProtectedRoute role="patient">
                <PatientLayout />
              </ProtectedRoute>
            }
          >  
            <Route path="patient/dashboard" element={<PatientDashboard />} />
            <Route path="patient/services" element={<Navigate to="/patient/dashboard" replace />} />
            <Route path="patient/services/:type" element={<ServiceDetail />} />
            <Route path="patient/services/provider/:id" element={<ProviderDetail />} />
            <Route path="patient/book/:providerId" element={<BookingPage />} />
            <Route path="patient/appointments" element={<Appointments />} />
            <Route path="patient/chats" element={<ChatAppointments />} />
            <Route path="patient/appointments/:id" element={<AppointmentDetail />} />
            <Route path="patient/notifications" element={<NotificationsPage />} />
            <Route path="patient/profile" element={<Profile />} />
            <Route path="patient/terms" element={<PatientTermsPage />} />
          </Route>

          {/* Patient Chat — full-screen, no sidebar */}
          <Route
            path="patient/chat/:appointmentId"
            element={
              <ProtectedRoute role="patient">
                <div className="h-[100dvh] flex flex-col">
                  <PatientChat />
                </div>
              </ProtectedRoute>
            }
          />

          {/* Provider Routes (Protected) */}
          <Route
            element={
              <ProtectedRoute role="provider">
                <ProviderLayout />
              </ProtectedRoute>
            }
          >
            <Route path="provider/dashboard"         element={<ProviderDashboard />} />
            <Route path="provider/appointments"        element={<ProviderAppointments />} />
            <Route path="provider/appointments/:id"    element={<ProviderAppointmentDetail />} />
            <Route path="provider/chats"               element={<ProviderChatAppointments />} />
            <Route path="provider/earnings"            element={<Navigate to="/provider/profile" replace state={{ view: 'payout' }} />} />
            <Route path="provider/reviews"             element={<Navigate to="/provider/profile" replace />} />
            <Route path="provider/profile"             element={<ProviderProfile />} />
            <Route path="provider/terms"               element={<ProviderTermsPage />} />
            <Route path="provider/support"             element={<ProviderProfile />} />
            <Route path="provider/notifications"       element={<ProviderNotificationsPage />} />
            <Route path="provider/induction"           element={<ProviderInduction />} />
          </Route>

          {/* Provider Chat — full-screen, no sidebar */}
          <Route
            path="provider/chat/:appointmentId"
            element={
              <ProtectedRoute role="provider">
                <div className="h-[100dvh] flex flex-col">
                  <ProviderChat />
                </div>
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  )
}
